import React from "react";
import {
  Apple,
  ArrowRight,
  BedDouble,
  CalendarClock,
  Car,
  CheckCircle2,
  Coins,
  Droplets,
  FileText,
  Globe2,
  HeartHandshake,
  LifeBuoy,
  MessageCircle,
  Plane,
  ShieldCheck,
  Sprout,
  Star,
  Trophy,
  Users,
  Wallet,
} from "lucide-react";
import { useReveal, useCountUp } from "../lib/useReveal";

export const ICONS = {
  Apple,
  BedDouble,
  CalendarClock,
  Car,
  CheckCircle2,
  Coins,
  Droplets,
  FileText,
  Globe2,
  HeartHandshake,
  LifeBuoy,
  MessageCircle,
  Plane,
  ShieldCheck,
  Sprout,
  Star,
  Trophy,
  Users,
  Wallet,
};

export function Icon({ name, className }) {
  const Cmp = ICONS[name] || CheckCircle2;
  return <Cmp className={className} />;
}

export function Reveal({ children, delay = 0, className = "" }) {
  const { ref, shown } = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`reveal ${shown ? "reveal-in" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

export function Eyebrow({ children, tone = "green" }) {
  const tones = {
    green: "bg-ivi-mint text-ivi-greenDark",
    white: "bg-white/15 text-white ring-1 ring-white/25",
    teal: "bg-ivi-teal/10 text-ivi-teal",
  };
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-heading text-[12px] font-bold uppercase tracking-[0.18em] ${tones[tone]}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, text, align = "center", tone = "green" }) {
  const alignCls = align === "center" ? "text-center mx-auto items-center" : "text-left items-start";
  return (
    <Reveal className={`flex max-w-3xl flex-col gap-5 ${alignCls}`}>
      {eyebrow && <Eyebrow tone={tone}>{eyebrow}</Eyebrow>}
      <h2 className="font-heading text-[34px] font-extrabold leading-[1.12] tracking-[-0.02em] text-ivi-slate sm:text-[44px]">
        {title}
      </h2>
      {text && <p className="text-[17px] leading-relaxed text-ivi-slateSoft">{text}</p>}
    </Reveal>
  );
}

export function PrimaryButton({ children, as = "button", className = "", ...rest }) {
  const Cmp = as;
  return (
    <Cmp
      className={`group inline-flex items-center justify-center gap-2 rounded-full bg-ivi-green px-7 py-3.5 font-heading text-[15px] font-bold text-white shadow-[0_16px_34px_-14px_rgba(90,158,47,0.95)] transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-ivi-greenDark hover:shadow-[0_20px_40px_-12px_rgba(90,158,47,0.8)] ${className}`}
      {...rest}
    >
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
    </Cmp>
  );
}

export function GhostButton({ children, className = "", ...rest }) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-full border border-ivi-slate/15 bg-white px-7 py-3.5 font-heading text-[15px] font-bold text-ivi-slate transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-ivi-green hover:bg-ivi-mint ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Counter({ value, suffix = "", className = "" }) {
  const { ref, value: v } = useCountUp(value);
  return (
    <span ref={ref} className={className}>
      {v.toLocaleString("en-US")}
      {suffix}
    </span>
  );
}

/** Hand-drawn circle marker like the original site's animated headline. */
export function CircleMark({ children, className = "" }) {
  const { ref, shown } = useReveal();
  return (
    <span ref={ref} className={`relative inline-block px-3 ${className}`}>
      <span className="relative z-10">{children}</span>
      <svg
        aria-hidden="true"
        viewBox="0 0 500 150"
        preserveAspectRatio="none"
        className="absolute -inset-x-5 -inset-y-3 h-[calc(100%+24px)] w-[calc(100%+40px)]"
      >
        <path
          d="M325,18C228.7-8.3,118.5,8.3,78,21C22.4,38.4,4.6,54.6,5.6,77.6c1.4,32.4,52.2,54,142.6,63.7 c66.2,7.1,212.2,7.5,273.5-8.3c64.4-16.6,104.3-57.6,33.8-98.2C386.7-4.9,179.4-1.4,126.3,20.7"
          fill="none"
          stroke="#99cc00"
          strokeWidth="7"
          strokeLinecap="round"
          style={{
            strokeDasharray: 1500,
            strokeDashoffset: shown ? 0 : 1500,
            transition: "stroke-dashoffset 1.4s cubic-bezier(0.22,1,0.36,1)",
          }}
        />
      </svg>
    </span>
  );
}
